export interface SystemEventBase {
	id: string
	['event_type']: string
	['event_time']: number
	['event_time_orig']?: number
}

export interface SystemEvent extends SystemEventBase {
	note?: string
	['parent_id']?: string
	['event_group_id']: string | null
	tags?: Array<string>
	timezone?: string
	['timezone_offset']?: string
	merged?: boolean
	duration?: number | null
}

export interface SystemBGEvent extends SystemEvent {
	mealreference?: string
	['start_before']?: string
	['end_before']?: string
	['start_after']?: string
	['end_after']?: string
	['start_none']?: string
	['end_none']?: string
	['start_bedtime']?: string
	['end_bedtime']?: string
	['start_fasting']?: string
	['end_fasting']?: string
	glucosevalue: string
	['measurement_state']: string
	['glucometer_id']?: string
}

export interface SystemBPEvent extends SystemEvent {
	diastolic: number | null
	['external_device_id']?: number | string | null
	pulse: number | null
	systolic: number | null
	range?: string
}

export interface SystemWeightEvent extends SystemEvent {
	weightvalue: string
	bmi: number
	weightStatus: string
}

export interface SystemStepEvent extends SystemEvent {
	steps: number
}

export interface SystemCarbsConsumptionEvent extends SystemEvent {
	carbsnetweightvalue: string
	['food_details']: SystemCarbsConsumptionFoodDetails
	foodenergyvalue: null | string | number
}

export interface SystemCarbsConsumptionFoodDetails {
	fatSecretFoodId: string | number | null
	fatSecretServingId: string | number | null
	fatSecretServingUrl: string | null
	imageUrl: string | null
	name: string | null
	servings: number | null
	servingNutrition: {
		[key: string]: {
			nutritionPerServing: number
			unitName: string
		}
	}
}

export interface SystemCaloriesBurnEvent extends SystemEvent {
	energyburntvalue: string
	['activity_name']: null | string
}

export interface SystemInsulinIntakeEvent extends SystemEvent {
	insulineunitsvalue: string
	['medication_type']: string
	['medication_name']: string
}

export type SystemHealthEvents =
	| SystemBGEvent
	| SystemBPEvent
	| SystemWeightEvent
	| SystemStepEvent
	| SystemCarbsConsumptionEvent
	| SystemCaloriesBurnEvent
	| SystemInsulinIntakeEvent
	| SystemMSKPlaylistItem

export interface SystemWeightEventLastMonth {
	bmiSum: number
	count: number
	weightSum: number
	events: Array<SystemWeightEvent>
}

export enum SYSTEM_EVENT_TYPES {
	GLUCOSE = 'glucosemeasurement',
	BLOOD_PRESSURE = 'bloodpressure',
	WEIGHT_CHANGE = 'weightchange',
	PEDOMETER = 'pedometer',
	CALORIES_BURN = 'caloriesburnt',
	INSULINE_INTAKE = 'insulineintake',
	CARBS_CONSUMPTION = 'carbsconsumption',
}

export interface SystemEventByType {
	count: number
	events: SystemEventsDataByType
}

export interface SystemEventsDataByType {
	[key: string]: Array<SystemBGEvent | SystemBPEvent>
}

export interface SystemEventsDataByGroup {
	prevMonth: SystemEventsDataByGroupItem
	curMonth: SystemEventsDataByGroupItem
	eventTypes: Array<string>
}

interface SystemEventsDataByGroupItem {
	count: number
	events: SystemEventsDataByType
	from: number
	to: number
}

export interface WeeklyEvents<T extends SystemHealthEvents> {
	[key: string]: {
		[key: string]: Array<T>
	}
}

export enum SYSTEM_BG_EVENT_STATE {
	LOW = 'low',
	HIGH = 'high',
	HYPER = 'hyper',
	IN_RANGE = 'in range',
	IN__RANGE = 'in_range',
	HYPO = 'hypo',
}

export enum SYSTEM_BG_ICON_TYPES {
	LOW = 'bgLow',
	HIGH = 'bgHigh',
	HYPER = 'bgHyper',
	IN_RANGE = 'bgInRange',
	HYPO = 'bgHypo',
}

export enum SYSTEM_BP_ICON_TYPES {
	ELEVATED = 'bpElevated',
	HYPER_BP = 'bpHyper',
	LOW = 'bpLow',
	NORMAL = 'bpNormal',
	HIGH_STG_1 = 'bpStage1',
	HIGH_STG_2 = 'bpStage2',
}

export enum SYSTEM_BP_STATE_TYPES {
	ELEVATED = 'Elevated',
	HYPER_BP = 'Hypertensive crisis',
	LOW = 'Low',
	NORMAL = 'Normal',
	HIGH_STG_1 = 'Stage 1',
	HIGH_STG_2 = 'Stage 2',
}

export enum SYSTEM_BP_STATE_KEYS {
	ELEVATED = 'ELEVATED',
	HYPER_BP = 'HYPER_BP',
	LOW = 'LOW',
	NORMAL = 'NORMAL',
	HIGH_STG_1 = 'HIGH_STG_1',
	HIGH_STG_2 = 'HIGH_STG_2',
}

export enum SYSTEM_WEIGHT_STATE_TYPES {
	UNDERWEIGHT = 'underweight',
	NORMAL = 'weight_normal',
	OVERWEIGHT = 'overweight',
	OBESE = 'obese',
	EXTREMELY_OBESE = 'extremely_obese',
}

export enum SYSTEM_WEIGHT_ICON_TYPES {
	UNDERWEIGHT = 'underweight',
	WEIGHT_NORMAL = 'weightNormal',
	OVERWEIGHT = 'overweight',
	OBESE = 'obese',
	EXTREMELY_OBESE = 'extremelyObese',
}

export interface EstimatedA1C {
	value: number
	factor: string
}

export interface SystemMSKPlaylistItem extends SystemEvent {
	['playlist_ordinal']: number
	['playlist_title']: string
	['playlist_code']: string
	items: Array<SystemMSKExercise>
}

export interface SystemMSKExercise {
	['item_ordinal']: number
	['item_code']: string
	['item_title']: string
	['item_type']: string
	actions: Array<SystemMSKAction>
}

export interface SystemMSKAction {
	['element_id']: number
	['dt_action']: string
	['action_type']: string
	exercise?: string
}

// Logbook

export interface SystemEventsLogbookState {
	// should update name to the stateAction for all states
	timeRange: Array<number>
	events: Array<SystemHealthEvents>
	filter: Array<string>
}

export enum SYSTEM_EVENTS_LOGBOOK_ACTION_TYPES {
	LOGBOOK_CHANGE_DATE = 'LOGBOOK_CHANGE_DATE',
	LOGBOOK_GET_EVENTS = 'LOGBOOK_GET_EVENTS',
	LOGBOOK_SET_FILTER = 'LOGBOOK_SET_FILTER',
	LOGBOOK_REMOVE_FILTER = 'LOGBOOK_REMOVE_FILTER',
}

export interface SystemEventsLogbookChangeDateRangeAction {
	type: SYSTEM_EVENTS_LOGBOOK_ACTION_TYPES.LOGBOOK_CHANGE_DATE
	payload: Array<number>
}

export interface SystemEventsLogbookGetEvents {
	type: SYSTEM_EVENTS_LOGBOOK_ACTION_TYPES.LOGBOOK_GET_EVENTS
	payload: Array<SystemHealthEvents>
}

export interface SystemEventsLogbookSetFilter {
	type: SYSTEM_EVENTS_LOGBOOK_ACTION_TYPES.LOGBOOK_SET_FILTER
	payload: string
}

export interface SystemEventsLogbookRemoveFilter {
	type: SYSTEM_EVENTS_LOGBOOK_ACTION_TYPES.LOGBOOK_REMOVE_FILTER
	payload: string
}

export type SystemEventsLogbookActions =
	| SystemEventsLogbookChangeDateRangeAction
	| SystemEventsLogbookGetEvents
	| SystemEventsLogbookSetFilter
	| SystemEventsLogbookRemoveFilter
